/**
 * Token Created Modal Component
 *
 * Shows newly created token plaintext (ONLY ONCE!).
 * Includes copy-to-clipboard functionality and warning.
 */
import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  CopyButton,
  Tooltip,
  ActionIcon,
  Paper,
  Code
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCopy,
  IconKey
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * TokenCreatedModal Component
 *
 * @param {boolean} opened - Modal open state
 * @param {Function} onClose - Close callback
 * @param {Object} tokenData - Created token data
 * @param {string} tokenData.plaintext_token - Full token string (shown once!)
 * @param {Object} tokenData.token - Token metadata
 */
const TokenCreatedModal = ({ opened, onClose, tokenData }) => {
  const [confirmed, setConfirmed] = useState(false);

  if (!tokenData) return null;

  const { plaintext_token, token } = tokenData;

  const handleClose = () => {
    if (!confirmed) {
      const shouldClose = window.confirm(
        'Вы уверены? Токен больше не будет показан. Убедитесь, что сохранили его!'
      );
      if (!shouldClose) return;
    }
    setConfirmed(false);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconKey size={24} color={tokens.colors.semantic.success.primary} />
          <Text fw={700} size="lg">
            Токен создан успешно
          </Text>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="lg">
        {/* Warning alert */}
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="ВАЖНО: Сохраните токен сейчас!"
          color="red"
          variant="filled"
        >
          <Text size="sm" c="white">
            Это единственный раз, когда вы увидите полный токен.
            После закрытия этого окна токен будет недоступен.
            Сохраните его в безопасном месте!
          </Text>
        </Alert>

        {/* Token info */}
        <Stack gap="xs">
          <Text size="sm" fw={600} c={tokens.colors.neutral[7]}>
            Название:
          </Text>
          <Text size="md" fw={500} c={tokens.colors.neutral[8]}>
            {token.name}
          </Text>
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={600} c={tokens.colors.neutral[7]}>
            Префикс (для идентификации):
          </Text>
          <Code
            style={{
              backgroundColor: tokens.colors.neutral[1],
              color: tokens.colors.neutral[8],
              padding: tokens.spacing.sm,
              fontSize: '14px'
            }}
          >
            {token.token_prefix}
          </Code>
        </Stack>

        {/* Token display with copy button */}
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={600} c={tokens.colors.neutral[7]}>
              API Token:
            </Text>
            <CopyButton value={plaintext_token} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? 'Скопировано!' : 'Копировать токен'}
                  withArrow
                  position="right"
                >
                  <ActionIcon
                    color={copied ? 'portal-green' : 'portal-blue'}
                    variant="filled"
                    onClick={copy}
                    size="lg"
                  >
                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>

          <Paper
            p="md"
            style={{
              backgroundColor: tokens.colors.neutral[1],
              border: `2px solid ${tokens.colors.brand.primary}`,
              wordBreak: 'break-all',
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
          >
            <Text c={tokens.colors.brand.primary} fw={600}>
              {plaintext_token}
            </Text>
          </Paper>
        </Stack>

        {/* Usage instructions */}
        <Alert
          icon={<IconKey size={16} />}
          title="Как использовать токен"
          color="blue"
          variant="light"
        >
          <Stack gap="xs">
            <Text size="sm">
              Добавьте этот токен в HTTP заголовок запросов:
            </Text>
            <Code
              block
              style={{
                backgroundColor: tokens.colors.neutral[0],
                padding: tokens.spacing.sm,
                fontSize: '12px'
              }}
            >
              X-API-Key: {plaintext_token}
            </Code>
          </Stack>
        </Alert>

        {/* Scopes info */}
        {token.scopes && token.scopes.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c={tokens.colors.neutral[7]}>
              Права доступа ({token.scopes.length}):
            </Text>
            <Stack gap={4}>
              {token.scopes.map((scope, index) => (
                <Text
                  key={index}
                  size="xs"
                  c={tokens.colors.neutral[6]}
                  style={{ fontFamily: 'Consolas, Monaco, monospace' }}
                >
                  • {scope}
                </Text>
              ))}
            </Stack>
          </Stack>
        )}

        {/* Expiration info */}
        {token.expires_at && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c={tokens.colors.neutral[7]}>
              Истекает:
            </Text>
            <Text size="sm" c={tokens.colors.semantic.warning.primary}>
              {new Date(token.expires_at).toLocaleString('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Moscow'
              })}
            </Text>
          </Stack>
        )}

        {/* Confirmation checkbox */}
        <Paper
          p="md"
          style={{
            backgroundColor: tokens.colors.semantic.success.primary + '10',
            border: `1px solid ${tokens.colors.semantic.success.primary}`
          }}
        >
          <Group>
            <input
              type="checkbox"
              id="token-saved-confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label
              htmlFor="token-saved-confirm"
              style={{
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: tokens.colors.neutral[8]
              }}
            >
              Я сохранил токен в безопасном месте
            </label>
          </Group>
        </Paper>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <CopyButton value={plaintext_token}>
            {({ copied, copy }) => (
              <Button
                variant="outline"
                color="portal-blue"
                leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                onClick={copy}
              >
                {copied ? 'Скопировано' : 'Копировать токен'}
              </Button>
            )}
          </CopyButton>

          <Button
            variant="filled"
            color="portal-green"
            onClick={handleClose}
            disabled={!confirmed}
          >
            Готово
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default TokenCreatedModal;
